import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DownloadableDocument } from '@app/core/business/downloadable-document';
import { OrderStatus } from '@app/core/business/order/order-status';
import { ApiService } from '@app/core/service/api-ws.service';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { Company } from './company';
import { AuthenticationService } from '../user/authentication.service';
import { FullContext } from '@app/core/service/context.service';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  private availableCompanies$ = new BehaviorSubject<Company[]>(null);

  private companiesMock: Company[] = [
    {
      code: 'pn',
      label: 'ProNatura',
      picture: {
        url:
          'http://www.pronatura.com/wp-content/uploads/2016/05/login-logo.png',
        alt: 'Logo ProNatura',
      },
      settings: {
        mainColor: '#711371',
        showOfferWithoutOrder: true,
        canAddProductStatuses: [OrderStatus.CREATED, OrderStatus.INACTIVE],
        canRemoveProductStatuses: [OrderStatus.CREATED, OrderStatus.INACTIVE],
        offerDefaultView: 'LIST',
        availabilityRefreshRate: 3,
        hasAdvertisement: true,
        advertisementRefreshRate: 5000,
        quickEntryAvailable: true,
        schedulerAvailable: true
      },
      terms: '',
      documents: [
        {
          label: 'Document 1',
          url: 'https://picturepan2.github.io/spectre/img/spectre-logo.svg',
        },
        {
          label: 'Document 2',
          url: 'https://picturepan2.github.io/spectre/img/spectre-logo.svg',
        },
        {
          label: 'Document 3',
          url: 'https://picturepan2.github.io/spectre/img/spectre-logo.svg',
        },
        {
          label: 'Document 4',
          url: 'https://picturepan2.github.io/spectre/img/spectre-logo.svg',
        },
        {
          label: 'Document 5',
          url: 'https://picturepan2.github.io/spectre/img/spectre-logo.svg',
        },
        {
          label: 'Document 6',
          url: 'https://picturepan2.github.io/spectre/img/spectre-logo.svg',
        },
      ],
      online: true,
      logoUrl: 'logoUrl1',
      colorClass: 'colorClass1',
    },
    {
      code: 'vf',
      label: 'VitaFrais',
      picture: {
        url: 'http://www.vitafrais.fr/sites/all/themes/vitafrais/logo.png',
        alt: 'Logo VitaFrais',
      },
      settings: {
        mainColor: '#015daa',
        showOfferWithoutOrder: false,
        canAddProductStatuses: [OrderStatus.CREATED],
        canRemoveProductStatuses: [OrderStatus.CREATED, OrderStatus.INACTIVE],
        offerDefaultView: 'THUMBNAILS',
        availabilityRefreshRate: 10,
        productListGroupingField: { fieldName: 'origin', fieldLabel: 'origin' },
        quickEntryAvailable: true,
        schedulerAvailable: true
      },
      terms: '',
      online: true,
      logoUrl: 'logoUrl2',
      colorClass: 'colorClass2'
    },
    {
      code: 'sg',
      label: 'Sully Group',
      picture: {
        url:
          'https://upload.wikimedia.org/wikipedia/fr/thumb/a/a5/Sully_Group.png/1200px-Sully_Group.png',
        alt: 'Logo Sully Group',
      },
      settings: {
        mainColor: 'red',
        showOfferWithoutOrder: false,
        canAddProductStatuses: [OrderStatus.CREATED, OrderStatus.INACTIVE],
        canRemoveProductStatuses: [OrderStatus.CREATED, OrderStatus.INACTIVE],
        offerDefaultView: 'SCHEDULER',
        availabilityRefreshRate: 200,
        quickEntryAvailable: true,
        schedulerAvailable: true
      },
      terms: '',
      online: true,
      logoUrl: 'logoUrl3',
      colorClass: 'colorClass3'
    },
  ];

  private companies = new BehaviorSubject<Company[]>([]);


  private documentMocks: DownloadableDocument[] = [
    {
      url: 'http://unec.edu.az/application/uploads/2014/12/pdf-sample.pdf',
      label: 'PDF Sample 1',
    },
    {
      url: 'http://africau.edu/images/default/sample.pdf',
      label: 'PDF Sample 2',
    },
  ];

  constructor(private apiService: ApiService) { // Reset context when logging out
  }

  /**
   * Get companies
   * @param userCode If present, returns only companies available for user
   */
  public getCompanies(userCode?: string): Observable<Company[]> {
    if (environment.useMock) {
      if (userCode) {
        return of(this.companiesMock).pipe(
          map(companies => companies.slice(0, 2)),
          delay(300)
        );
      } else {
        return of(this.companiesMock).pipe(delay(500));
      }
    }

    if (userCode) {
      const params = new HttpParams({
        fromObject: {
          userCode: userCode,
        },
      });
    }
    return this.apiService.getCompanies(userCode);
  }

  public getDocumentsCGV(companyCode: string): Observable<Blob> {
    return this.apiService.GetDocumentsCGV(companyCode);
  }


  public getAvailableCompanies(): Company[] {
    return this.availableCompanies$.value;
  }


  public getAvailableCompanies$(): Observable<Company[]> {
    return this.availableCompanies$.asObservable();
  }


  public notifyAvailableCompanies(companies: Company[]): void {
    this.availableCompanies$.next(companies);
  }

  public updateAvailableCompanies(fullContext: FullContext, userCode: string): void {
    this.getCompanies(userCode).subscribe(companies => {
      companies = companies.filter(c => c.code !== (fullContext.company && fullContext.company.code ? fullContext.company.code : ''));

      this.notifyAvailableCompanies(companies);
    });
  }

  /**
   * Get company by its code
   * @param companyCode Code of the company
   */
  public getCompany(companyCode: string): Observable<Company> {
    if (environment.useMock) {
      return of(this.companiesMock).pipe(
        map(companies => companies.find(c => c.code === companyCode)),
        delay(50)
      );
    }
    return this.apiService.getCompany(companyCode);
  }

  /**
   * Get a company's marketing documents
   * @param companyCode Code of the company
   */
  public getDocuments(companyCode: string, aideALaVente = false): Observable<DownloadableDocument[]> {
    if (environment.useMock) {
      return of(this.documentMocks).pipe(delay(100));
    }
    return this.apiService.getDocumentsFromCompanyCode(companyCode, aideALaVente);
  }



}
